import { AxiosResponse } from "axios"
import { useApiCommand } from "hooks/core/useApiCommand"
import {
  generateExportMap,
  exportWithMap,
  exportCSV,
  listExportMapProfiles,
  saveExportMapProfile,
  getExportMapProfile,
  deleteExportMapProfile,
  getOriginalMapFile,
  ExportWithMapOptions,
} from "api/Engine/exportMap"
import { QueryInstance } from "src/types/engine.types"
import { EXPORT_VARIABLE_TYPES, CSV_CAT_OPTION } from "src/types/engineExport.types"
import { ExportMapProfile } from "src/types/exportMap.types"

/**
 * Return type interface for the useExportMap hook
 */
interface UseExportMapReturn {
  /** Generate export map CSV from query instance */
  generateMap: (
    instanceId: QueryInstance,
    varType?: EXPORT_VARIABLE_TYPES
  ) => Promise<AxiosResponse<string>>
  isGeneratingMap: boolean

  /** Export data with custom export map applied */
  exportWithCustomMap: (
    instanceId: QueryInstance,
    exportMapCsv: string,
    options?: ExportWithMapOptions
  ) => Promise<AxiosResponse<Blob>>
  isExportingWithMap: boolean

  /** Export data as CSV directly (without custom export map) */
  exportCsvDirect: (
    instanceId: QueryInstance,
    varType?: EXPORT_VARIABLE_TYPES,
    catOption?: CSV_CAT_OPTION,
    filename?: string
  ) => Promise<AxiosResponse<Blob>>
  isExportingCsvDirect: boolean

  /** List saved export map profiles for a project */
  listProfiles: (projectId: string) => Promise<AxiosResponse<ExportMapProfile[]>>
  isListingProfiles: boolean

  /** Save export map as a named profile */
  saveProfile: (
    projectId: string,
    profileName: string,
    exportMapCsv: string
  ) => Promise<AxiosResponse<unknown>>
  isSavingProfile: boolean

  /** Load export map profile by name */
  getProfile: (
    projectId: string,
    profileName: string
  ) => Promise<AxiosResponse<string>>
  isLoadingProfile: boolean

  /** Delete export map profile */
  deleteProfile: (
    projectId: string,
    profileName: string
  ) => Promise<AxiosResponse<unknown>>
  isDeletingProfile: boolean

  /** Get original map.csv from imported data */
  getOriginalMap: (projectId: string) => Promise<AxiosResponse<string>>
  isLoadingOriginalMap: boolean
}

/**
 * Custom hook for export map operations.
 *
 * Provides functions for generating, saving, loading, and managing export map profiles.
 * Export maps control variable names and category codes during SPSS/CSV data export.
 *
 * @returns Object containing export map operation functions and their loading states
 *
 * @example
 * const {
 *   generateMap,
 *   isGeneratingMap,
 *   postMap,
 *   saveProfile,
 *   listProfiles,
 * } = useExportMap();
 *
 * // Generate export map from query instance
 * const csvResponse = await generateMap(queryInstanceId);
 *
 * // Export with custom map applied
 * await exportWithCustomMap(queryInstanceId, editedCsv, { format: "sps" });
 *
 * // Save as named profile
 * await saveProfile(projectId, "my_profile", editedCsv);
 */
export const useExportMap = (): UseExportMapReturn => {
  const [generateMap, isGeneratingMap] = useApiCommand(generateExportMap)
  const [exportWithCustomMap, isExportingWithMap] = useApiCommand(exportWithMap)
  const [exportCsvDirect, isExportingCsvDirect] = useApiCommand(exportCSV)
  const [listProfiles, isListingProfiles] = useApiCommand(listExportMapProfiles)
  const [saveProfile, isSavingProfile] = useApiCommand(saveExportMapProfile)
  const [getProfile, isLoadingProfile] = useApiCommand(getExportMapProfile)
  const [deleteProfile, isDeletingProfile] = useApiCommand(deleteExportMapProfile)
  const [getOriginalMap, isLoadingOriginalMap] = useApiCommand(getOriginalMapFile)

  return {
    generateMap,
    isGeneratingMap,
    exportWithCustomMap,
    isExportingWithMap,
    exportCsvDirect,
    isExportingCsvDirect,
    listProfiles,
    isListingProfiles,
    saveProfile,
    isSavingProfile,
    getProfile,
    isLoadingProfile,
    deleteProfile,
    isDeletingProfile,
    getOriginalMap,
    isLoadingOriginalMap,
  }
}
